import { useEffect } from 'react'
import {
  RIGHT_PANEL_CLOSE_REQUEST_EVENT,
  RIGHT_PANEL_OPEN_REQUEST_EVENT,
  type RightPanelCloseRequestDetail,
  type RightPanelOpenRequestDetail,
} from '@/lib/panels/right-panel-events'

interface UseRightPanelWindowEventsInput {
  isMobile: boolean
  isOpen: boolean
  activePanel: string | null
  setIsOpen: (open: boolean) => void
  setActivePanel: (panel: string | null) => void
  closePanel: () => void
  onPanelActivate?: () => void
  onPanelOpened?: () => void
  onClosedByRequest?: () => void
}

/**
 * The window-event bridges between `RightPanel` and the rest of the editor: the left panel and
 * the Select tool closing it, open/close requests from outside the rail, and telling the left
 * panel to close when this one opens on mobile.
 */
export function useRightPanelWindowEvents({
  isMobile,
  isOpen,
  activePanel,
  setIsOpen,
  setActivePanel,
  closePanel,
  onPanelActivate,
  onPanelOpened,
  onClosedByRequest,
}: UseRightPanelWindowEventsInput) {
  // Auto-close on mobile when left opens
  useEffect(() => {
    const onLeftOpen = () => {
      if (isMobile) {
        closePanel()
      }
    }

    window.addEventListener('left-panel-opened', onLeftOpen)
    return () => window.removeEventListener('left-panel-opened', onLeftOpen)
  }, [isMobile, closePanel])

  // Close when user activates Select tool via v/V so focus stays on left (object properties) panel
  useEffect(() => {
    const onSelectToolActivated = () => {
      closePanel()
    }

    window.addEventListener('select-tool-activated', onSelectToolActivated)
    return () => window.removeEventListener('select-tool-activated', onSelectToolActivated)
  }, [closePanel])

  // Opened from outside the rail (e.g. the canvas context menu's "Adjust background") — same as
  // clicking that panel's rail icon, except it never toggles an already-open panel closed.
  useEffect(() => {
    const onOpenRequest = (e: Event) => {
      const panel = (e as CustomEvent<RightPanelOpenRequestDetail>).detail?.panel

      if (!panel) return

      onPanelActivate?.()
      setActivePanel(panel)
      setIsOpen(true)
      onPanelOpened?.()
    }

    window.addEventListener(RIGHT_PANEL_OPEN_REQUEST_EVENT, onOpenRequest)
    return () => window.removeEventListener(RIGHT_PANEL_OPEN_REQUEST_EVENT, onOpenRequest)
  }, [onPanelActivate, onPanelOpened, setActivePanel, setIsOpen])

  // Closed from outside the rail (e.g. the menu bar's Previous/Next spread buttons). A request
  // naming a panel closes it only when it is the one showing, so a different open panel is never
  // closed by it; a request with no panel closes whichever one is showing.
  useEffect(() => {
    if (!isOpen || !activePanel) return

    const onCloseRequest = (e: Event) => {
      const panel = (e as CustomEvent<RightPanelCloseRequestDetail>).detail?.panel

      if (panel !== undefined && panel !== activePanel) return

      closePanel()
      onClosedByRequest?.()
    }

    window.addEventListener(RIGHT_PANEL_CLOSE_REQUEST_EVENT, onCloseRequest)
    return () => window.removeEventListener(RIGHT_PANEL_CLOSE_REQUEST_EVENT, onCloseRequest)
  }, [isOpen, activePanel, closePanel, onClosedByRequest])

  // Notify when right opens on mobile to close left
  useEffect(() => {
    if (isMobile && isOpen) {
      window.dispatchEvent(new Event('right-panel-opened'))
    }
  }, [isMobile, isOpen])
}
